/**
 * Flow C patient intent creation — POST /api/consults/{id}/intents.
 *
 * The tagged response has ONE arm (contract §5.1): `mode: "redirect"` leaves the SPA for
 * Stripe hosted Checkout, and this hook drives the navigation itself, since there is
 * nothing further for a caller to render once the browser is leaving. A second
 * `mode: "element"` arm was documented for a Payment Element form that was never
 * reachable from any screen; the subtree that would have consumed it
 * (`gateway/checkout/*`, `usePaymentIntent`) is deleted, and authorisation is hosted
 * Checkout with manual capture, full stop. `result.mode` is still READ rather than
 * assumed — an untagged or unknown arm simply does not navigate, and is handed back to
 * the caller instead of being followed blindly.
 *
 * `gatewayApi` is an optional trailing param defaulting to the module singleton,
 * mirroring the component-level DI rule (contract §9).
 */
import { useCallback, useState } from "react";
import defaultGatewayApi from "../api";
import { extractGatewayErrorCode } from "../errors";

export function useConsultIntent(appointmentId, gatewayApi = defaultGatewayApi) {
  const [data, setData] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const createIntent = useCallback(
    async (body) => {
      setCreating(true);
      setError(null);
      try {
        const result = await gatewayApi.createConsultIntent(appointmentId, body);
        setData(result);
        if (result?.mode === "redirect" && result?.checkout_url) {
          window.location.href = result.checkout_url;
        }
        return { ok: true, data: result };
      } catch (e) {
        const code = extractGatewayErrorCode(e);
        setError({ code, raw: e });
        return { ok: false, code };
      } finally {
        setCreating(false);
      }
    },
    [appointmentId, gatewayApi],
  );

  return { data, creating, error, createIntent };
}

export default useConsultIntent;
